import axios from "axios"
import { subscriptionsPath, trialsPath } from "@/api/v2Paths.js"
import { chargebeeCreate, chargebeeUpdate } from "@/chargebee.js"
import { toast } from "@/toast"
import { queryClient } from "@/query.js"
import { shareInFlight } from "@/shareInFlight.js"
import { thirdPartyTrackingApi } from "@/tracking.js"

const path = ext => ext ? `/customer/subscriptions/${ext}` : "/customer/subscriptions"
// Chargebee-free endpoints, served by cdk-salesforce under the v2 root (the plain
// /customer/subscriptions above is Chargebee's, served by sls-subscriptions). cdk-salesforce is the
// source of truth for these, since Salesforce can't be queried.
//
// Reads go to subscriptions and return every kind it holds — the trial/paid split matters to a write,
// not to someone listing what they have. Writes go to trials, because starting or ending a trial is
// not the same operation as buying or cancelling a paid subscription.
const salesforcePath = subscriptionsPath
const salesforceTrialPath = trialsPath
// Our subscriptions carry this prefix and Chargebee's do not, which is how a write picks its
// backend. sls-supporters routes on the same prefix.
export const isSalesforceSubscription = id => String(id || "").startsWith("pphm-")

// The cutover switch: until it is on, trials and purchases go to Chargebee; after it, trials are
// started in cdk-salesforce and the buy button opens the Salesforce checkout. Reading and managing
// subscriptions is not switched, so turning it off again never hides a Salesforce subscription.
const isRcaCheckoutEnabled = () => import.meta.env.VITE_RCA_CHECKOUT_ENABLED === "true"
export const RCA_CHECKOUT_ENABLED = isRcaCheckoutEnabled()
// The Chargebee trial endpoint of sls-subscriptions, used while the switch is off.
const chargebeeTrialPath = "/customer/subscriptions/trial"
const checkStackStatusCreateOrUpdate = function (stackId) {
  return axios.post(`/customer/stacks/${stackId}/status`, { accepted_states: ["CREATE_COMPLETE", "UPDATE_COMPLETE"] })
}
const checkInstanceStatusRunning = function (stackId) {
  return axios.post(`/customer/instances/${stackId}/status`, { accepted_states: ["RUNNING"] })
}

// Stack statuses that represent a final state — polling must stop when any of these is reached.
//   ready                  — instance is live
//   failed                 — provisioning failed
//   blocked_sanction_review — compliance hold, CS must review before provisioning can proceed
export const TERMINAL_STACK_STATUSES = new Set(["ready", "failed", "blocked_sanction_review"])

const subscription = {
  namespaced: true,
  state: {
    subscriptions: [],
    polls: new Set(),
    isCached: false,
    retryCount: 0,
  },
  mutations: {
    ADD_POLL(state, id) {
      state.polls.add(id)
    },
    CLEAR_POLL(state, { id }) {
      state.polls.delete(id)
    },
    CLEAR_CACHE(state) {
      state.isCached = false
    },
    SET_SUBSCRIPTIONS(state, subscriptions) {
      state.subscriptions = subscriptions
      state.isCached = true
    },
    ADD_SUBSCRIPTION(state, subscription) {
      state.subscriptions = [...state.subscriptions, subscription]
    },
    UPDATE_SUBSCRIPTION(state, subscription) {
      state.subscriptions = state.subscriptions.map(sub => sub.id === subscription.id ? subscription : sub)
    },
    REMOVE_SUBSCRIPTION(state, { id }) {
      state.subscriptions = state.subscriptions.filter(sub => sub.id !== id)
    },
    INCREMENT_RETRY_COUNT(state) {
      state.retryCount++
    },
    CLEAR_RETRY_COUNT(state) {
      state.retryCount = 0
    },
  },

  actions: {
    pollSubscription({ state, commit }, { id }) {
      if (state.polls.has(id)) {
        return
      }

      // Read from the backend that holds the subscription: the overview shows both kinds.
      const subscriptionPath = isSalesforceSubscription(id) ? salesforcePath(id) : path(id)

      const poll = async () => {
        try {
          const { data } = await axios.get(subscriptionPath)
          const subscription = state.subscriptions.find(subscription => subscription.id === id)

          // Invalidate permissions cache only when transitioning specifically to "ready" —
          // the other terminal states (failed, blocked_sanction_review) do not provision
          // an instance, so no permission refresh is needed.
          if (subscription.stackStatus !== "ready" && data.stackStatus === "ready") {
            await queryClient.invalidateQueries({ queryKey: ["fetchAllPermissions"], refetchType: "active" })
          }

          commit("UPDATE_SUBSCRIPTION", data)

          if (TERMINAL_STACK_STATUSES.has(data.stackStatus)) {
            // Terminal state reached — stop polling regardless of which one it is.
            commit("CLEAR_POLL", data)
            commit("CLEAR_RETRY_COUNT")
          }
          else {
            setTimeout(poll, 5000)
            commit("ADD_POLL", id)
          }
        }
        catch (e) {
          if (e.response && e.response.status === 403 && state.retryCount < 10) {
            commit("INCREMENT_RETRY_COUNT")
            setTimeout(poll, 5000)
            commit("ADD_POLL", id)
          }
          else {
            commit("CLEAR_POLL", { id })
            commit("CLEAR_RETRY_COUNT")
            toast.error(e)
          }
        }
      }

      return poll()
    },
    // Both providers during the migration: existing customers are billed by Chargebee, new business
    // by Salesforce. A list that does not answer is left out and not cached, so the next visit asks
    // again; only the cards are shared for now, actions still pick their backend by id.
    fetchSubscriptions({ state, dispatch }) {
      if (state.isCached) {
        return
      }
      return shareInFlight("subscriptions", () => dispatch("loadSubscriptions"))
    },
    async loadSubscriptions({ commit }) {
      const [salesforce, chargebee] = await Promise.allSettled([axios.get(salesforcePath()), axios.get(path())])
      const failed = [salesforce, chargebee].find(({ status }) => status === "rejected")
      if (failed) {
        toast.error(failed.reason)
      }
      if (salesforce.status === "rejected" && chargebee.status === "rejected") {
        return
      }
      try {
        const data = [...(chargebee.value?.data || []), ...(salesforce.value?.data || [])]
        commit("SET_SUBSCRIPTIONS", data)
        if (failed) {
          commit("CLEAR_CACHE")
        }

        thirdPartyTrackingApi({
          params: {
            sub_id: data.map(s => s.id).join(","),
            action: "fetch",
            date: Date.now(),
          },
        })
      }
      catch (e) {
        toast.error(e)
      }
    },
    // Puts a subscription ordered through the checkout (the order response carries it) into the
    // store. The list is loaded first so the overview's own fetch does not replace it with a list
    // that, read from an eventually consistent index, may not have the new record yet.
    async addOrderedSubscription({ state, commit, dispatch }, subscription) {
      await dispatch("fetchSubscriptions")
      commit(state.subscriptions.some(sub => sub.id === subscription.id) ? "UPDATE_SUBSCRIPTION" : "ADD_SUBSCRIPTION", subscription)
    },
    async createSubscription({ commit }, { subdomain, id, name, region, tester }) {
      // Returns the created subscription on success (callers inspect stackStatus,
      // e.g. CSubscription.vue checks for "blocked_sanction_review") or null on failure.
      try {
        const { data } = await chargebeeCreate(subdomain, id, region, tester)
        commit("ADD_SUBSCRIPTION", data)
        toast.success(`Subscription ${name} created`)
        // https://jira.paesslergmbh.de/browse/PPHM-529:
        // Chargebee doesn't send this wanted information automatically in their integration
        window.dataLayer?.push({ event: "subscription", subscriptionId: data.id })
        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "create",
            date: Date.now(),
          },
        })
        return data
      }
      catch (e) {
        toast.error(e)
        return null
      }
    },
    async createTrial({ dispatch }, params) {
      return dispatch(isRcaCheckoutEnabled() ? "createSalesforceTrial" : "createChargebeeTrial", params)
    },
    async createChargebeeTrial({ commit }, { subdomain, planId, companyName, country, region, shareUsageData }) {
      try {
        const { data } = await axios.post(chargebeeTrialPath, { subdomain, planId, companyName, country, region, shareUsageData })
        commit("ADD_SUBSCRIPTION", data.subscription)
        toast.success("Subscription trial created")
        window.dataLayer?.push({
          event: "cb_event",
          cbAction: "lead",
          cbCategory: "engagement",
        })
        window.dataLayer?.push({
          event: "subscription",
          subscriptionId: data.subscription.id,
          stackId: data.subscription.stackId,
        })
        thirdPartyTrackingApi({
          params: {
            sub_id: data.subscription.id,
            action: "create",
            date: Date.now(),
          },
        })
        return true
      }
      catch (e) {
        toast.error(e)
        return false
      }
    },
    async createSalesforceTrial(
      { commit, dispatch, rootState },
      { subdomain, planId, region, companyName, tester, shareUsageData },
    ) {
      try {
        // cdk-salesforce derives the customer from the auth token and mints the id/stack_name; it
        // composes the fqdn from the subdomain the user picked, and forwards company to the Salesforce lead.
        // tester=true (non-prod only) yields a subscription/lead without a real PRTG stack.
        //
        // The currency goes with it so the Salesforce lead is created in the one the visitor was
        // shown prices in. There is no billing address at trial time, so the IP-derived visitor
        // currency is the only signal there is; without it the lead takes the org default and a
        // European prospect ends up quoted in USD.
        const { data } = await axios.post(salesforceTrialPath(), {
          plan_id: planId, region, company: companyName, subdomain, tester,
          data_sharing_consent: shareUsageData,
          currency: rootState.visitor?.visitor?.currencyCode,
        })
        commit("ADD_SUBSCRIPTION", data)
        dispatch("pollSubscription", { id: data.id })
        toast.success("Trial requested")
        window.dataLayer?.push({
          event: "cb_event",
          cbAction: "lead",
          cbCategory: "engagement",
        })
        window.dataLayer?.push({
          event: "subscription",
          subscriptionId: data.id,
          stackId: data.stackId,
        })
        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "create",
            date: Date.now(),
          },
        })
        return true
      }
      catch (e) {
        toast.error(e)
        return false
      }
    },
    async updateSubscription({ commit, dispatch }, { id, planId, name, stackId }) {
      try {
        await Promise.all([
          checkInstanceStatusRunning(stackId),
          checkStackStatusCreateOrUpdate(stackId),
        ])
        const { data } = await chargebeeUpdate(id, planId)
        commit("UPDATE_SUBSCRIPTION", data)
        dispatch("pollSubscription", { id })
        toast.success(`Subscription updated to ${name}`)

        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "update",
            date: Date.now(),
          },
        })
      }
      catch (e) {
        toast.error(e)
      }
    },
    async cancelSalesforceTrial({ commit }, { id, cancelReasonCode }) {
      // Cancels a trial via cdk-salesforce (tears down the stack, ends the Salesforce trial).
      // The reason code uses the same query param name as the paid-subscription cancel.
      try {
        const { data } = await axios.delete(
          `${salesforceTrialPath(id)}?subscription_cancel_reason_code=${encodeURIComponent(cancelReasonCode ?? "")}`,
        )
        commit("REMOVE_SUBSCRIPTION", { id })
        commit("instance/REMOVE_TIMEZONE", { id }, { root: true })
        toast.success("Trial cancelled")
        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "cancel",
            date: Date.now(),
          },
        })
        return true
      }
      catch (e) {
        toast.error(e)
        return false
      }
    },
    async cancelSubscription({ commit }, { id, cancelReasonCode }) {
      // the returned booleans are needed later to determine if to route back to '/' or not
      try {
        // Salesforce-backed subscriptions cancel at the end of their term through cdk-salesforce;
        // Chargebee's go to sls-subscriptions. Sending a pphm- id to Chargebee's endpoint is a
        // guaranteed failure, which is what happened before this split.
        const cancelPath = isSalesforceSubscription(id) ? salesforcePath(id) : path(id)
        const { data } = await axios.delete(`${cancelPath}?subscription_cancel_reason_code=${cancelReasonCode}`)
        if (data.subscriptionStatus === "cancelled") {
          commit("REMOVE_SUBSCRIPTION", data)
          commit("instance/REMOVE_TIMEZONE", data, { root: true })
        }
        else {
          commit("UPDATE_SUBSCRIPTION", data)
        }
        toast.success("Subscription cancelled")

        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "cancel",
            date: Date.now(),
          },
        })
        return true
      }
      catch (e) {
        toast.error(e)
        return false
      }
    },
    async restoreSubscription({ commit }, { id }) {
      try {
        const restorePath = isSalesforceSubscription(id) ? salesforcePath(`${id}/reactivate`) : path(`${id}/reactivate`)
        await axios.post(restorePath)
        const { data } = await axios.get(isSalesforceSubscription(id) ? salesforcePath(id) : path(id))
        commit("UPDATE_SUBSCRIPTION", data)
        toast.success("Subscription has been reactivated")

        thirdPartyTrackingApi({
          params: {
            sub_id: data.id,
            action: "update",
            date: Date.now(),
          },
        })
      }
      catch (e) {
        toast.error(e)
      }
    },
    async changeSubdomain({ commit, dispatch }, { id, subdomain, stackId }) {
      try {
        // Salesforce-backed (trial) subscriptions have no Chargebee record to round-trip through,
        // so their FQDN is changed directly at stack level instead. pollSubscription's v2 read
        // already covers both kinds of subscriptions, so the same refresh works here too.
        if (isSalesforceSubscription(id)) {
          await checkStackStatusCreateOrUpdate(stackId)
          await axios.put(`/customer/stacks/${stackId}/fqdn`, { subdomain })
          dispatch("pollSubscription", { id })
          return
        }

        await Promise.all([
          checkInstanceStatusRunning(stackId),
          checkStackStatusCreateOrUpdate(stackId),
        ])
        const { data } = await axios.put(path(`${id}/subdomain`), { subdomain })
        commit("UPDATE_SUBSCRIPTION", data)
        dispatch("pollSubscription", data)
      }
      catch (e) {
        toast.clear() // validateDomain() will trigger a toast, so without clearing it, the error will not be shown
        toast.error(e)
      }
    },
    checkIfExists({ state }, id) {
      if (state.subscriptions.some(sub => sub.id === id)) {
        return Promise.resolve(`Subscription ${id} already stored.`)
      }
      const subscriptionPath = isSalesforceSubscription(id) ? salesforcePath(id) : path(id)
      return axios.get(subscriptionPath)
    },
  },
}

export {
  subscription,
}
